/** The header's restech chip (plan §1.1): which reservation system feeds the booking,
 *  through which account, and when its feed last shaped it ("fed 2 hours ago", from the
 *  feed fingerprint). The tooltip names the source, the import mode, the last run and
 *  the pause while the booking is checked in — what `suspended_reason` states: the feed's
 *  WEBHOOKS pause, while a bookings sync may still import it, which the state verdict's
 *  evidence says in the server's words. The chip opens the Integration tab. Absent for a
 *  booking no restech manages. */
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingRecordRestech } from '@/api/bookingRecord';
import { formatRelative, formatZoned } from '@/utils/zonedTime';

const SUSPENDED: Record<string, string> = {
  checkin_local: 'checked in by the guide app',
  checkin_dashboard: 'checked in on the dashboard',
  checkin_console: 'checked in in the console',
};

export function RestechChip({
  restech,
  timezone,
  integrationHref,
}: {
  restech: BookingRecordRestech;
  timezone: string;
  /** The Integration tab of this booking. */
  integrationHref: string;
}) {
  if (!restech.managed_by) return null;
  const name = restech.managed_by_display ?? restech.managed_by;
  const fed = restech.fingerprint_ts ? `fed ${formatRelative(restech.fingerprint_ts)}` : 'not fed since tracking began';
  const suspended = restech.suspended_reason ? SUSPENDED[restech.suspended_reason] ?? 'checked in' : null;
  const source = restech.source;
  const lines = [
    source?.source_id ? `Source ${source.source_id}${source.origin ? ` (${source.origin})` : ''}` : null,
    restech.mode ? `Import mode ${restech.mode.toUpperCase()}` : null,
    source?.last_run_ts ? `Last run ${formatZoned(source.last_run_ts, timezone)}` : null,
    restech.fingerprint_ts
      ? `What the feed sent: ${formatZoned(restech.fingerprint_ts, timezone)}${restech.fingerprint_status === 'backfilled' ? ' (reconstructed)' : ''}`
      : 'What the feed sent is not recorded for this booking yet',
    suspended ? `Webhook updates paused — ${suspended}` : null,
    suspended ? restech.verdicts.booking_state?.evidence || 'Cancellations and no-shows still arrive' : null,
    'Open the Integration tab',
  ].filter(Boolean) as string[];

  return (
    <Tooltip
      title={
        <Box sx={{ display: 'grid', gap: 0.25 }}>
          {lines.map((l) => (
            <Typography key={l} variant="caption" component="div">
              {l}
            </Typography>
          ))}
        </Box>
      }
    >
      <Chip
        size="small"
        variant="outlined"
        clickable
        component={RouterLink}
        to={integrationHref}
        icon={suspended ? <PauseCircleOutlineIcon /> : <SyncIcon />}
        data-restech-chip
        aria-label={`${name}${restech.account ? `, account ${restech.account}` : ''}, ${suspended ? 'webhooks paused' : fed}. ${lines.join('. ')}`}
        label={
          <span>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {name}
            </Box>
            {restech.account ? ` · ${restech.account}` : ''} · {suspended ? 'webhooks paused' : fed}
          </span>
        }
        sx={{ color: 'text.secondary', fontWeight: 500 }}
      />
    </Tooltip>
  );
}
